import React, { useState, useEffect, useRef } from 'react';
import { TextValidator, SelectValidator, ValidatorForm } from 'react-material-ui-form-validator';
import Grid from '@material-ui/core/Grid';
import MenuItem from '@mui/material/MenuItem';
import Button from '@mui/material/Button';
import { CircularProgress } from '@mui/material';
import AlertDialog from '../../../../../../components/AlertDialog/index.es6';
import {
  getLocalNumberLength,
  getPartner,
  isVAlidBotswanaNationalId
} from '../../../../../../lib/access.es6';
import { detectChangedFields } from '../utils/validationHelpers';

/**
 * Modal for editing member details
 */
const EditMemberModal = ({ show, member, onSave, onClose, isProcessing }) => {
  const [firstname, setFirstname] = useState('');
  const [middlename, setMiddleName] = useState('');
  const [lastname, setLastname] = useState('');
  const [gender, setGender] = useState('');
  const [phonenumber, setPhoneNumber] = useState('');
  const [nationalid, setNationalId] = useState('');
  const [passport, setPassport] = useState('');
  const [postalAddress, setpostalAddress] = useState('');
  const [residentialAddress, setresidentialAddress] = useState('');
  const [nationalIdError, setNationalIdError] = useState(null);

  const formRef = useRef(null);
  const internationalCallingCode = getPartner().country.international_calling_code;

  // Populate form when member changes
  useEffect(() => {
    if (member) {
      setFirstname(member.first_name || '');
      setMiddleName(member.middle_name || '');
      setLastname(member.last_name || '');
      setGender(member.gender || '');

      // Clean phone number (remove country code if present)
      let cleanPhone = member.msisdn || '';
      if (cleanPhone && cleanPhone !== 'N/A') {
        cleanPhone = cleanPhone.replace(internationalCallingCode, '').replace('+', '');
      } else {
        cleanPhone = '';
      }
      setPhoneNumber(cleanPhone);

      setNationalId(member.national_id && member.national_id !== 'N/A' ? member.national_id : '');
      setPassport(member.passport && member.passport !== 'N/A' ? member.passport : '');
      setpostalAddress(member.postal_address || '');
      setresidentialAddress(member.residential_address || '');
      setNationalIdError(null);
    }
  }, [member, internationalCallingCode]);

  // Setup validation rules
  useEffect(() => {
    ValidatorForm.addValidationRule('isValidPhoneNumber', value => {
      if (!value || value.trim() === '') return true; // Optional
      return value.length === getLocalNumberLength();
    });

    ValidatorForm.addValidationRule('isValidNationalId', (value) => {
      if (!value || value.trim() === '') {
        setNationalIdError(null);
        return true; // Optional
      }

      const partnerGuid = getPartner().guid;
      if (partnerGuid === 'aspinplus') {
        // South African validation would go here
        return true;
      }

      const result = isVAlidBotswanaNationalId(value);
      if (result.isValid) {
        setNationalIdError(null);
        return true;
      } else {
        setNationalIdError(result.error || 'Invalid National ID format');
        return false;
      }
    });

    return function cleanup() {
      ValidatorForm.removeValidationRule('isValidPhoneNumber');
      ValidatorForm.removeValidationRule('isValidNationalId');
    };
  }, []);

  const handleSubmit = () => {
    // Build updated member object
    const updatedMember = {
      ...member,
      first_name: firstname.trim(),
      middle_name: middlename.trim(),
      last_name: lastname.trim(),
      gender: gender,
      msisdn: phonenumber ? internationalCallingCode + phonenumber : 'N/A',
      national_id: nationalid.trim() || 'N/A',
      passport: passport.trim() || 'N/A',
      postal_address: postalAddress.trim(),
      residential_address: residentialAddress.trim()
    };

    // Detect changes
    const changes = detectChangedFields(member, updatedMember);

    if (changes.length === 0) {
      alert('No changes detected. Please modify at least one field.');
      return;
    }

    onSave(updatedMember, changes);
  };

  const handleClose = () => {
    setNationalIdError(null);
    onClose();
  };

  if (!member) return null;

  return (
    <AlertDialog
      custom
      show={show}
      onClose={handleClose}
      size="lg"
      showCancel={false}
      showConfirm={false}
      showTitle={false}
      style={{
        marginTop: '0',
        top: '50%',
        transform: 'translateY(-50%)',
        maxHeight: '90vh',
        overflow: 'visible',
        width: '900px',
        maxWidth: '90vw'
      }}
    >
      <div style={{ padding: '30px', maxHeight: '82vh', overflowY: 'auto' }}>
        <h2 style={{ marginBottom: '8px', color: '#032A37' }}>
          Edit Member Details
        </h2>
        <p style={{ marginBottom: '20px', color: '#666', fontSize: '14px' }}>
          {member.first_name} {member.last_name} ({member.relationship})
        </p>

        <ValidatorForm ref={formRef} onSubmit={handleSubmit} instantValidate={false}>
          <Grid container spacing={3}>
            {/* First Name */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="First Name"
                onChange={(e) => setFirstname(e.target.value)}
                name="firstname"
                value={firstname}
                validators={['required']}
                errorMessages={['First name is required']}
                variant="outlined"
              />
            </Grid>

            {/* Middle Name */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="Middle Name (Optional)"
                onChange={(e) => setMiddleName(e.target.value)}
                name="middlename"
                value={middlename}
                variant="outlined"
              />
            </Grid>

            {/* Last Name */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="Last Name"
                onChange={(e) => setLastname(e.target.value)}
                name="lastname"
                value={lastname}
                validators={['required']}
                errorMessages={['Last name is required']}
                variant="outlined"
              />
            </Grid>

            {/* Gender */}
            <Grid item xs={12} md={6}>
              <SelectValidator
                fullWidth
                label="Gender"
                onChange={(e) => setGender(e.target.value)}
                name="gender"
                value={gender}
                validators={['required']}
                errorMessages={['Gender is required']}
                variant="outlined"
                SelectProps={{
                  MenuProps: {
                    disablePortal: false,
                    style: { zIndex: 99999 },
                    anchorOrigin: {
                      vertical: "bottom",
                      horizontal: "left"
                    },
                    transformOrigin: {
                      vertical: "top",
                      horizontal: "left"
                    },
                    getContentAnchorEl: null
                  }
                }}
              >
                <MenuItem value="Male">Male</MenuItem>
                <MenuItem value="Female">Female</MenuItem>
              </SelectValidator>
            </Grid>

            {/* Phone Number */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="Phone Number (Optional)"
                onChange={(e) => setPhoneNumber(e.target.value)}
                name="phonenumber"
                value={phonenumber}
                validators={phonenumber ? ['isValidPhoneNumber'] : []}
                errorMessages={[`Phone number must be ${getLocalNumberLength()} digits`]}
                variant="outlined"
              />
            </Grid>

            {/* National ID */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="National ID (Optional)"
                onChange={(e) => setNationalId(e.target.value)}
                name="nationalid"
                value={nationalid}
                validators={['isValidNationalId']}
                errorMessages={[nationalIdError || 'Invalid National ID format']}
                variant="outlined"
              />
            </Grid>

            {/* Passport */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="Passport (Optional)"
                onChange={(e) => setPassport(e.target.value)}
                name="passport"
                value={passport}
                variant="outlined"
              />
            </Grid>

            {/* Residential Address */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="Residential Address (Optional)"
                onChange={(e) => setresidentialAddress(e.target.value)}
                name="residentialAddress"
                value={residentialAddress}
                variant="outlined"
              />
            </Grid>

            {/* Postal Address */}
            <Grid item xs={12} md={6}>
              <TextValidator
                fullWidth
                label="Postal Address (Optional)"
                onChange={(e) => setpostalAddress(e.target.value)}
                name="postalAddress"
                value={postalAddress}
                variant="outlined"
              />
            </Grid>
          </Grid>

          {/* Info Box */}
          <div style={{
            marginTop: '20px',
            padding: '15px',
            backgroundColor: '#e7f3ff',
            border: '1px solid #b3d9ff',
            borderRadius: '8px',
            fontSize: '14px',
            color: '#032A37'
          }}>
            <strong>Note:</strong> Changing these details will not affect the member's premium or benefits.
          </div>

          {/* Action Buttons */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
            <Button
              variant="outlined"
              onClick={handleClose}
              disabled={isProcessing}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isProcessing}
              style={{ backgroundColor: '#032A37', color: '#fff' }}
            >
              {isProcessing ? <CircularProgress size={24} /> : 'Save Changes'}
            </Button>
          </div>
        </ValidatorForm>
      </div>
    </AlertDialog>
  );
};

export default EditMemberModal;
